// redux/profileSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { fetchUserProfile, updateUserProfile } from '../services/supabaseClient/profileService';

// 🚀 Load from localStorage if available
const loadProfileFromStorage = () => {
  const cached = localStorage.getItem('profile');
  return cached ? JSON.parse(cached) : null;
};

// 📥 Async thunk to fetch user profile by Supabase ID
export const fetchStudentProfileThunk = createAsyncThunk(
  'profile/fetchProfile',
  async (userId, { rejectWithValue }) => {
    try {
      const { data, error } = await fetchUserProfile(userId);
      if (error) return rejectWithValue(error.message);

      localStorage.setItem('profile', JSON.stringify(data));
      return data;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

// 📤 Async thunk to update student profile
export const updateStudentProfileThunk = createAsyncThunk(
  'profile/updateProfile',
  async (profileData, { rejectWithValue }) => {
    try {
      const { data, error } = await updateUserProfile(profileData);
      if (error) return rejectWithValue(error.message);

      localStorage.setItem('profile', JSON.stringify(data));
      return data;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

const profileSlice = createSlice({
  name: 'profile',
  initialState: {
    profile: loadProfileFromStorage(),
    loading: false,
    error: null,
  },
  reducers: {
    setStudentProfile: (state, action) => {
      state.profile = action.payload;
      localStorage.setItem('profile', JSON.stringify(action.payload));
    },
    updateStudentProfile: (state, action) => {
      const updated = {
        ...state.profile,
        ...action.payload,
        lastUpdated: new Date().toISOString(),
      };
      state.profile = updated;
      localStorage.setItem('profile', JSON.stringify(updated));
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchStudentProfileThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStudentProfileThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.profile = action.payload;
      })
      .addCase(fetchStudentProfileThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateStudentProfileThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateStudentProfileThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.profile = action.payload;
      })
      .addCase(updateStudentProfileThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { setStudentProfile, updateStudentProfile } = profileSlice.actions;
export default profileSlice.reducer;

// ✅ Selector
export const selectProfile = (state) => state.profile.profile;
export const selectProfileLoading = (state) => state.profile.loading;
export const selectProfileError = (state) => state.profile.error;
